import React, { useState, useMemo, useRef, useEffect } from "react";
import {
  FileText,
  User,
  Building2,
  Plus,
  TrendingUp,
  Target,
  Users,
  UserCheck,
  Laptop,
  Calculator,
  ChevronDown,
  Check,
  Search,
  ClipboardList,
} from "lucide-react";

// Ordre d'affichage des regroupements dans le sélecteur.
const CATEGORY_ORDER = [
  "MISSIONS PRINCIPALES",
  "JURIDIQUE",
  "SOCIAL",
  "EXPERTISE/CONSEIL",
  "BON DE COMMANDE",
];

const letterOptions = [
  {
    value: "LM Globale SCTE.docx",
    label: "LM Globale SCTE",
    category: "MISSIONS PRINCIPALES",
    icon: Building2,
  },
  {
    value: "LM Globale EI.docx",
    label: "LM Globale EI",
    category: "MISSIONS PRINCIPALES",
    icon: User,
  },
  {
    value: "LM BNC.docx",
    label: "LM BNC",
    category: "MISSIONS PRINCIPALES",
    icon: FileText,
  },
  {
    value: "LM micro entreprise.docx",
    label: "LM Micro-entreprise",
    category: "MISSIONS PRINCIPALES",
    icon: FileText,
  },
  {
    value: "LM Création de société.docx",
    label: "LM Création de société",
    category: "JURIDIQUE",
    icon: Plus,
  },
  {
    value: "LM Sécrétariat juridique.docx",
    label: "LM Sécrétariat juridique",
    category: "JURIDIQUE",
    icon: FileText,
  },
  {
    value: "LM sociale.docx",
    label: "LM Sociale",
    category: "SOCIAL",
    icon: Users,
  },
  {
    value: "LM social ponctuelle.docx",
    label: "LM Social ponctuelle",
    category: "SOCIAL",
    icon: UserCheck,
  },
  {
    value: "LM Prévisionnel.docx",
    label: "LM Prévisionnel",
    category: "EXPERTISE/CONSEIL",
    icon: TrendingUp,
  },
  {
    value: "LM Evaluation.docx",
    label: "LM Evaluation",
    category: "EXPERTISE/CONSEIL",
    icon: Target,
  },
  {
    value: "LM Situation.docx",
    label: "LM Situation",
    category: "EXPERTISE/CONSEIL",
    icon: TrendingUp,
  },
  {
    value: "LM Audit d'acquisition.docx",
    label: "LM Audit d'acquisition",
    category: "EXPERTISE/CONSEIL",
    icon: Search,
  },
  // Saisie libre : missions sans assurance > autres prestations fournies à
  // l'entité, quand aucune autre LM ne convient (DocTypes/LMLIBRE).
  {
    value: "LM libre autres prestations.docx",
    label: "LM libre - autres prestations (sans assurance)",
    category: "EXPERTISE/CONSEIL",
    icon: ClipboardList,
  },
  // Non listé explicitement par l'utilisateur dans le regroupement demandé —
  // conservé sous EXPERTISE/CONSEIL (fiscal/conseil) plutôt que supprimé.
  {
    value: "LM IR.docx",
    label: "LM IR",
    category: "EXPERTISE/CONSEIL",
    icon: Calculator,
  },
  {
    value: "LM BG Logiciel.docx",
    label: "BC Logiciel",
    category: "BON DE COMMANDE",
    icon: Laptop,
  },
  {
    value: "BON DE COMMANDE.docx",
    label: "BC Atelier professionnel",
    category: "BON DE COMMANDE",
    icon: ClipboardList,
  },
  {
    value: "LM BC RBE.docx",
    label: "BC RBE",
    category: "BON DE COMMANDE",
    icon: ClipboardList,
  },
];

export default function LetterSelect({
  selectedForm,
  handleFormChange,
  disabled,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);

  const filteredOptions = useMemo(() => {
    return letterOptions.filter(
      (opt) =>
        opt.label.toLowerCase().includes(search.toLowerCase()) ||
        opt.category.toLowerCase().includes(search.toLowerCase())
    );
  }, [search]);

  // Regroupement par catégorie, dans l'ordre défini par CATEGORY_ORDER —
  // une catégorie sans résultat (après filtre recherche) n'est pas affichée.
  const groupedOptions = useMemo(() => {
    return CATEGORY_ORDER.map((category) => ({
      category,
      options: filteredOptions.filter((opt) => opt.category === category),
    })).filter((group) => group.options.length > 0);
  }, [filteredOptions]);

  const selectedOption = letterOptions.find(
    (opt) => opt.value === selectedForm
  );

  // Fermer le dropdown si clic à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <div
        className={`flex  bg-white items-center justify-between h-14 px-4 border-2 rounded-lg cursor-pointer transition-all ${
          selectedOption
            ? "border-[#8B1D41] bg-[#8B1D41]/5 hover:bg-[#8B1D41]/10"
            : "border-slate-200 hover:border-slate-300"
        } ${open ? "ring-2 ring-[#8B1D41]/20" : ""}`}
        onClick={() => !disabled && setOpen(!open)}
      >
        <div className="flex items-center gap-3">
          {selectedForm !== "" && selectedOption ? (
            <>
              <div className="w-10 h-10 rounded-lg bg-[#8B1D41] flex items-center justify-center">
                <selectedOption.icon className="w-5 h-5 text-white" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-slate-800">
                  {selectedOption.label}
                </p>
                <p className="text-xs text-slate-500">
                  {selectedOption.category}
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                <FileText className="w-5 h-5 text-slate-400" />
              </div>
              <span className="text-slate-500">
                Sélectionnez le type de lettre de mission
              </span>
            </>
          )}
        </div>
        <ChevronDown
          className={`w-5 h-5 text-slate-400 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </div>

      {open && (
        <div className="absolute z-10 mt-2 w-full bg-white border-2 border-slate-200 rounded-lg shadow-lg max-h-80 overflow-y-auto">
          <div className="p-3 border-b border-slate-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Rechercher un modèle..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 h-10 w-full border border-slate-200 rounded-lg focus:outline-none focus:border-[#8B1D41] focus:ring-1 focus:ring-[#8B1D41]/20"
              />
            </div>
          </div>

          <div className="p-2">
            {groupedOptions.length === 0 ? (
              <div className="p-8 text-center text-slate-500 text-sm">
                Aucun modèle trouvé
              </div>
            ) : (
              groupedOptions.map((group) => (
                <div key={group.category} className="mb-2">
                  <p className="px-3 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wide text-[#8B1D41]/70">
                    {group.category}
                  </p>
                  {group.options.map((opt) => {
                    const isSelected = selectedForm === opt.value;
                    return (
                      <div
                        key={opt.value}
                        onClick={() => {
                          handleFormChange({ target: { value: opt.value } });
                          setOpen(false);
                        }}
                        className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer mb-1 transition-all ${
                          isSelected
                            ? "bg-[#8B1D41]/10 border-2 border-[#8B1D41]"
                            : "hover:bg-slate-50 border-2 border-transparent"
                        }`}
                      >
                        <div
                          className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            isSelected
                              ? "bg-[#8B1D41] text-white"
                              : "bg-slate-100 text-slate-600"
                          }`}
                        >
                          <opt.icon className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p
                            className={`font-semibold text-sm truncate ${
                              isSelected ? "text-[#8B1D41]" : "text-slate-800"
                            }`}
                          >
                            {opt.label}
                          </p>
                        </div>
                        {isSelected && (
                          <Check className="w-5 h-5 text-[#8B1D41]" />
                        )}
                      </div>
                    );
                  })}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
